import React from "react";
import { ArrowLeft, Crown, Zap, Bot, Check } from "lucide-react";
import { type BackendUserWithPlan, type BackendPlan } from "../userService";

type PlansViewProps = {
  plans: BackendPlan[];
  userData?: BackendUserWithPlan | null;
  onBack: () => void;
};

const PlansView: React.FC<PlansViewProps> = ({ plans, userData, onBack }) => {
  const maxPrecedence = plans.length > 0 ? Math.max(...plans.map(p => p.precedence || 0)) : 0;

  // Find user's current plan precedence
  const activePlan = userData?.active_plan?.plan;
  let currentPrecedence = 0;
  if (activePlan) {
    currentPrecedence = activePlan.precedence || 0;
  } else {
    // Fallback to basic plan precedence
    const basicPlan = plans.find(p => p.name.toLowerCase().includes("basic") || parseFloat(p.price) === 0);
    currentPrecedence = basicPlan?.precedence || 0;
  }

  return (
    <div className="profile-view">
      {/* Header */}
      <header className="profile-header">
        <button
          type="button"
          className="back-btn"
          onClick={onBack}
          aria-label="Back to Plan Management"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="header-content">
          <span className="header-icon">
            <Crown size={24} />
          </span>
          <h1 className="header-title">Available Plans</h1>
        </div>
        <div className="header-actions"></div>
      </header>

      <div className="profile-content">
        {/* Available Plans Section */}
        <section className="available-plans-section">
          <h2 className="section-title">Choose a Plan</h2>

          <div className="plans-grid">
            {plans.map((plan) => {
              // Determine if this is the user's current plan
              let isCurrent = false;
              const activePlanId = userData?.active_plan?.plan?.id;
              
              if (activePlanId === plan.id) {
                  isCurrent = true;
              } else if (!activePlanId) {
                  // If user has no active plan, the "Basic" or free plan is their current one
                  if (plan.name.toLowerCase().includes("basic") || parseFloat(plan.price) === 0) {
                      isCurrent = true;
                  }
              }

              const isHighestPrecedence = plan.precedence === maxPrecedence && maxPrecedence > 0;
              const isHigherPrecedence = (plan.precedence || 0) > currentPrecedence;

              // Map billing interval: 1 -> month
              const intervalLabel = plan.billing_interval === 1 ? "month" : plan.billing_interval;

              return (
                <div
                  key={plan.id}
                  className={`plan-card ${isHighestPrecedence ? "featured" : ""} ${isCurrent ? "current" : ""}`}
                >
                  {isHighestPrecedence && <div className="plan-card-badge">Most Popular</div>}
                  <div className="plan-card-header">
                    <div className={`plan-card-icon ${isHighestPrecedence ? "premium" : "basic"}`}>
                      {isHighestPrecedence ? <Zap size={24} /> : <Bot size={24} />}
                    </div>
                    <div className="plan-card-title">
                      <h3>{plan.name}</h3>
                    </div>
                  </div>
                  <div className="plan-card-price">
                    <span className="price-amount">
                      {parseFloat(plan.price) === 0 ? "Free" : `${plan.currency === 'AUD' ? '$' : ''}${Math.round(parseFloat(plan.price))}`}
                    </span>
                    {intervalLabel && parseFloat(plan.price) !== 0 && (
                      <span className="price-period">/{intervalLabel}</span>
                    )}
                    {!intervalLabel && parseFloat(plan.price) !== 0 && (
                      <span className="price-period">{plan.currency} one-time</span>
                    )}
                  </div>
                  <ul className="plan-card-features">
                    {plan.features.map((feature, idx) => (
                      <li key={idx}>
                        <Check size={14} /> {feature}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <button
                      type="button"
                      className="plan-card-btn current"
                      disabled={true}
                    >
                      Current Plan
                    </button>
                  ) : (isHigherPrecedence && plan.checkout_url) ? (
                    <button
                      type="button"
                      className={`plan-card-btn ${isHighestPrecedence ? "primary" : "secondary"}`}
                      onClick={() => {
                        const path = plan.checkout_url!;
                        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
                        window.location.href = `${import.meta.env.VITE_API_URL}/${normalizedPath}`;
                      }}
                    >
                      Upgrade
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PlansView;
